/**
 * `CartStore` — the order contents, as the BFF holds them.
 *
 * ### A cache, not a second source of truth
 *
 * `MigrationDraftStore` owns client state and persists it. This store owns
 * nothing: every field it exposes is read off the last `Cart` the BFF returned,
 * and every mutation replaces that snapshot wholesale with the server's reply.
 * Nothing is written here that was not read from there.
 *
 * That is why it does **not** persist. A cart that survived a reload in
 * `sessionStorage` would be a second copy of a value the BFF already keeps, free
 * to disagree with it about the totals — and the totals are what the customer
 * agrees a price from. On a reload the snapshot is empty and `ensureLoaded()`
 * fetches it again. The mock BFF persists the cart itself, so nothing is lost.
 *
 * ### Where it appears in the journey
 *
 * The cart is created at S-06, not at S-03: `AddToCartInput.simOption` is
 * non-null and the SIM option is not answered until S-06, so there is nothing to
 * call `addToCart` with before then. `MigrationDraftStore` holds the offer, the
 * device and the instalment plan until they can all be sent together — see that
 * file's header, and `docs/SCREEN-MAP.md`.
 *
 * One exception, and it is the BFF's: `verifyOtp` writes the verified number onto
 * a cart record, creating one if there is none. So by the time S-06 runs, a cart
 * usually exists already with an offer of `null` and a SIM option of `null`. That
 * is what lets S-06 restore a SIM option the customer chose before pressing Back.
 *
 * ### Violations are not failures
 *
 * `Cart.violations` is never empty in this POC — `POC-PREPAID-CREDIT` (Q-28) is
 * pushed onto every cart, because what happens to the remaining prepaid credit on
 * migration is undefined and a cart that said nothing about it would imply it had
 * been handled. So no guard and no screen may treat a non-empty `violations` as a
 * reason to stop. They are disclosures to render, which is S-07's job.
 */
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AddToCartDocument,
  CartDocument,
  PlaceOrderDocument,
  UpdateCheckoutDocument,
} from '../../../generated/graphql';
import type {
  AddToCartInput,
  CartPartsFragment,
  CheckoutInput,
  OrderPartsFragment,
  PlaceOrderInput,
} from '../../../generated/graphql';
import type { AppError, Result } from '../domain/errors';
import { Errors, err, ok } from '../domain/errors';
import { GraphqlClient } from '../graphql/graphql-client';

@Injectable({ providedIn: 'root' })
export class CartStore {
  private readonly gql = inject(GraphqlClient);

  private readonly snapshot = signal<CartPartsFragment | null>(null);
  private readonly failure = signal<AppError | null>(null);

  /** Set once the first read has come back — including when it came back empty. */
  private loaded = false;

  /** Shared by concurrent callers, so a guard and a screen do not both fetch. */
  private inFlight: Promise<void> | null = null;

  readonly cart = this.snapshot.asReadonly();

  /** The last read failure. Mutation failures are returned, not stored. */
  readonly error = this.failure.asReadonly();

  readonly simOption = computed(() => this.snapshot()?.simOption ?? null);
  readonly paymentMethod = computed(() => this.snapshot()?.paymentMethod ?? null);
  readonly deliveryOption = computed(() => this.snapshot()?.deliveryOption ?? null);
  readonly deliveryAddress = computed(() => this.snapshot()?.deliveryAddress ?? null);

  /**
   * Read, never derived. The field's name and its schema description contradict
   * each other and the BFF resolved that in favour of the name (see
   * `mock-bff/resolvers/index.ts` and `docs/SCREEN-MAP.md`); a client that
   * re-derived it from the SIM option would be a second answer to the same
   * question, free to drift from the first.
   */
  readonly requiresDelivery = computed(() => this.snapshot()?.requiresDelivery ?? false);

  /** Disclosures to render, not reasons to stop. See the header. */
  readonly violations = computed(() => this.snapshot()?.violations ?? []);

  /**
   * Fetch the cart once per page load. Safe to call from a guard and from the
   * screen it guards — the second caller awaits the first one's request.
   */
  ensureLoaded(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    this.inFlight ??= this.read();
    return this.inFlight;
  }

  /** After a change made somewhere other than this store, or to retry a failed read. */
  reload(): Promise<void> {
    this.loaded = false;
    this.inFlight = null;
    return this.ensureLoaded();
  }

  /**
   * S-06. The offer, the device, the instalment plan and the SIM option go in
   * together, because the schema will not take them separately.
   *
   * The failure is returned rather than stored: `addToCart` can fail four ways
   * with four different presentations — a rule violation is an inline notice, a
   * handset that has gone out of stock is a block, an outage is a system error —
   * and only the calling screen knows where to put each. Returning a `Result`
   * keeps that decision on the screen, next to the markup.
   */
  async add(input: AddToCartInput): Promise<Result<CartPartsFragment, AppError>> {
    const { data, errors } = await firstValueFrom(this.gql.request(AddToCartDocument, { input }));

    const first = errors[0];
    if (first) return err(first);

    const cart = data?.addToCart;
    if (!cart) {
      // `addToCart: Cart!` is non-null, so an absent cart with no error entry is
      // something graphql-js cannot produce. Reported as "no cart came back"
      // rather than given a code of its own — an INLINE retry is the least
      // inventive reading of a response that says nothing.
      return err(Errors.CART_EXPIRED);
    }

    this.accept(cart);
    return ok(cart);
  }

  /**
   * S-08. The payment method, and the delivery option and address when anything
   * ships.
   *
   * The BFF decides what "when anything ships" means and clears both fields itself
   * on an order with no parcel, so a screen that sends them anyway is not creating
   * a delivery where there is none. What it will not do is invent an address: with
   * `requiresDelivery` true and none supplied it returns `ADDRESS_REQUIRED`
   * (E4004, FIELD, Q-26) — which is why this is a `Result` like `add()` rather than
   * a stored error. That failure belongs on the address field, and only S-08 knows
   * where the field is.
   */
  async checkout(input: CheckoutInput): Promise<Result<CartPartsFragment, AppError>> {
    const { data, errors } = await firstValueFrom(
      this.gql.request(UpdateCheckoutDocument, { input }),
    );

    const first = errors[0];
    if (first) return err(first);

    const cart = data?.updateCheckout;
    if (!cart) return err(Errors.CART_EXPIRED);

    this.accept(cart);
    return ok(cart);
  }

  /**
   * S-09. The commitment point.
   *
   * `idempotencyKey` comes from `MigrationDraftStore`, which generates one per
   * draft and keeps it across retries — so a double submission that gets past the
   * interface returns the order already placed rather than placing a second one.
   * No idempotency model is specified upstream; the BRD raises the double-order
   * problem at S-09 without answering it, and `PlaceOrderInput`'s own description
   * says so.
   *
   * `termsAcceptedVersion` is sent only by the variant of S-09 that collects an
   * acceptance (Q-23). The BFF stores it without validating it, so the rule that
   * an order cannot be placed without acceptance is the screen's to enforce.
   *
   * The cart is cleared server-side on success, so the local snapshot is dropped
   * with it. Holding the contents of an order that no longer exists as a cart
   * would let a Back navigation render a checkout for it.
   */
  async placeOrder(input: PlaceOrderInput): Promise<Result<OrderPartsFragment, AppError>> {
    const { data, errors } = await firstValueFrom(this.gql.request(PlaceOrderDocument, { input }));

    const first = errors[0];
    if (first) return err(first);

    const order = data?.placeOrder;
    if (!order) return err(Errors.CART_EXPIRED);

    this.accept(null);
    return ok(order);
  }

  private async read(): Promise<void> {
    const { data, errors } = await firstValueFrom(this.gql.request(CartDocument));

    // Marked loaded either way: a failed read must not make `ensureLoaded()`
    // retry on every navigation. `reload()` is the way back.
    this.loaded = true;

    const first = errors[0];
    if (first) {
      this.failure.set(first);
      return;
    }

    // `cart: Cart` is nullable and null is a legitimate answer — there is no cart
    // until something is put in one.
    this.accept(data?.cart ?? null);
  }

  private accept(cart: CartPartsFragment | null): void {
    this.snapshot.set(cart);
    this.failure.set(null);
    this.loaded = true;
  }
}
